import api from 'api';
import Privileges from '../lib/constants/privileges';
import colors from 'lib/constants/resourceColors';

const form = [
    {key: 'transaction_id',
     displayKey: ['transaction_number'],
        label: 'Transaction #',
        createOnly: true,
        type: 'autocomplete',
        link: { url: '/transactions/:transaction_id', privileges: Privileges.READ_TRANSACTIONS },
        suggestFormat: (t) => {
            let text = `${t.transaction_number} (${t.customer_name}) ${t.memo ? `[Available: $${t.available_credit}]` : ''}`;
            return {value: t.id, text}
        },
        suggestUrl: '/transactions',
        suggestFields: ['id', 'transaction_number', 'memo', 'available_credit', 'customer_name']},
    {key: 'check_number', static: true},
    {label: 'Credit Card Last 4 Digits', key: 'last_digits', static: true},
    {key: 'invoice_id',
     displayKey: ['invoice_number'],
        label: 'Invoice #',
        createOnly: true,
        type: 'autocomplete',
        link: { url: '/invoices/:invoice_id', privileges: Privileges.READ_INVOICES },
        suggestFormat: (i) => {
            let text = `${i.invoice_number} (${i.customer_name}) ${i.description ? `(${i.description})` : ''}`;
            return {value: i.id, text}
        },
        suggestUrl: '/invoices',
        suggestFields: ['id', 'invoice_number', 'description', 'customer_name']},
    {type: 'currency',
        key: 'amount',
        required: true,
        // Set on create; only editable afterward by users holding write_invoice_payments_amount
        // (typo correction). Backend enforces the same privilege on the update payload and refuses
        // any edit that over-extends the paying transaction's available credit.
        updatePrivileges: [Privileges.WRITE_INVOICE_PAYMENTS_AMOUNT]},
    {key: 'payment_date', label: 'Payment Date', type: 'date'},
    'memo',
    {type: 'file',
       key: 'transaction_file_url',
        urlKey: 'transaction_file_url',
        label: 'Attachment', 
        static: true},
    {key: 'voided', static: true, readOnly: true,  type: 'boolean'},
];

export default {
    defaults: {
        name: 'Invoice Payment',
        color: colors.invoicePayments,
        formFields: form,
    },
    view: {
        // Surfaces the History panel on the invoice-payment detail screen. The backend captures
        // every field edit (amount, memo) as an autogenerated 'update' remark keyed on the
        // invoice_payment id via remarkUpdateHook; this block makes those captured changes visible.
        // object_type must match the backend remarks config ('invoice_payment'). Note: value edits
        // are hidden until the user ticks 'Show Edit History' (create/delete always show).
        remarks: {
            header: (ip) => ip.invoice_number,
            object_type: 'invoice_payment',
            object_id_key: 'id',
        },
        formActions: [
            {   label: 'Void Invoice Payment',
                icon: 'ban',
                // Only offer Void on a live application.
                visible: ({values}) => !values.voided,
                // Gated on the auto-seeded RPC_voidInvoicePayment privilege (superuser bypasses).
                // Soft-void: flags this single application voided, which reopens the invoice and
                // returns the credit to the paying transaction. The transaction stays live.
                privileges: [Privileges.VOID_INVOICE_PAYMENT],
                confirm: {
                    title: 'Void Invoice Payment',
                    content: 'Void this invoice payment? It will be marked voided — the invoice '
                           + 'reopens for that amount and the credit returns to the paying '
                           + 'transaction. The payment (transaction) itself stays active.',
                    okText: 'Void Invoice Payment',
                    okType: 'danger',
                },
                method: async ({values, reload, loading, success, error}) => {
                    const invoice_payment_id = values.id;

                    loading('Voiding');
                    let result;
                    try {
                        result = await api.rpc('voidInvoicePayment', {invoice_payment_id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (result.success) {
                        success({
                            title: 'Invoice Payment Voided',
                            content: 'This invoice payment was voided. The invoice reopens for that '
                                   + 'amount and the credit is available on the paying transaction.',
                            okText: 'Ok',
                        });
                    } else {
                        const rpcError = result.errors && result.errors.error;
                        error({
                            title: 'Could Not Void',
                            content: (rpcError && rpcError.message)
                                   || 'The invoice payment could not be voided.',
                            okText: 'Ok',
                        });
                    }

                    reload(true);
                }
            },
            {   label: 'Unvoid Invoice Payment',
                icon: 'undo',
                // Only offer Unvoid on a voided application.
                visible: ({values}) => !!values.voided,
                // Gated on the auto-seeded RPC_unvoidInvoicePayment privilege (superuser bypasses).
                // Un-voids this single application; because all math derives off the flag, the
                // invoice re-counts the payment and the credit is re-committed on the paying
                // transaction. If that credit was re-applied elsewhere while voided, the backend
                // credit-overrun guard refuses the un-void and its message is shown below.
                privileges: [Privileges.UNVOID_INVOICE_PAYMENT],
                confirm: {
                    title: 'Unvoid Invoice Payment',
                    content: 'Unvoid this invoice payment? It becomes active again — the invoice '
                           + 'counts this amount as paid and the credit is re-committed on the '
                           + 'paying transaction.',
                    okText: 'Unvoid Invoice Payment',
                    okType: 'primary',
                },
                method: async ({values, reload, loading, success, error}) => {
                    const invoice_payment_id = values.id;

                    loading('Unvoiding');
                    let result;
                    try {
                        result = await api.rpc('unvoidInvoicePayment', {invoice_payment_id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (result.success) {
                        success({
                            title: 'Invoice Payment Unvoided',
                            content: 'This invoice payment is active again. The invoice counts it as '
                                   + 'paid and the credit is re-committed on the paying transaction.',
                            okText: 'Ok',
                        });
                    } else {
                        const rpcError = result.errors && result.errors.error;
                        error({
                            title: 'Could Not Unvoid',
                            content: (rpcError && rpcError.message)
                                   || 'The invoice payment could not be unvoided.',
                            okText: 'Ok',
                        });
                    }

                    reload(true);
                }
            },
        ],
    },
    search: {
        fields: ['id', 'created_at', 'invoice_number', 'check_number', 'last_digits', 'memo', 'transaction_number', 'transaction_created_at', 'amount', 'memo', 'paying_customer_name', 'transaction_file_url', 'voided'],
        filterOptions: [
            {label: 'Invoice Payments', filters: {voided: false}, default: true},
            {label: 'Voided', filters: {voided: true}},
            {label: 'All', filters: null},
        ],
        searchBy: ['customer name', 'memos', 'check_number', 'last four digits', 'amount', 'transaction number'],
        links: [
            'new',
        ],
        tableProps: {
            rowURL: '/invoice_payments/:id',
            columns: [
                {accessor: 'created_at', type: 'date', heading: 'Invoice Payment Date', cellWidth: '100px'},
                {accessor: 'paying_customer_name', heading: 'Customer', cellWidth: '200px'},
                {accessor: 'invoice_number', cellWidth: '100px', style: 'bold'},
                {heading: 'Transaction Details', type:'multiple', cellWidth: '220px', rows: [
                    {accessor: 'transaction_number'},
                    {label: 'Check #', style:'subtext', accessor:'check_number'},
                    {label: 'Last Four Digits CC', style: 'subtext', accessor:'last_digits'},
                    {label: 'Memo', style: 'subtext', accessor: 'transaction_memo'},
                    {label: 'Total Amount', type:'currency', style: 'subtext', accessor: 'amount'},
                    {label: 'Date', type: 'date', style: 'subtext', accessor: 'transaction_created_at'}
                ]},
                {heading: 'Payment Amount', accessor: 'amount', type: 'currency', cellWidth: '150px'},
                {accessor: 'memo', heading: 'Payment Memo'},
                {heading: 'Voided', accessor: 'voided', type: 'boolean', cellWidth: '70px'},
                {type: 'file', key: 'transaction_file_url', label: 'View File'}
            ]
        }
    }
}


